import { useCallback, useState } from "react";

export type SaveCurrentChangesResult = { ok: true; reason?: never } | { ok: false; reason: "validation" | "submit" };

interface UseConfirmLanguageSwitchParams {
    currentLanguageCode: string | null;
    isDirty: boolean;
    saveCurrentChanges: () => Promise<SaveCurrentChangesResult>;
    applyLanguage: (nextLanguageCode: string) => void;
}

export const useConfirmLanguageSwitch = ({
    currentLanguageCode,
    isDirty,
    saveCurrentChanges,
    applyLanguage,
}: UseConfirmLanguageSwitchParams) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingLanguageCode, setPendingLanguageCode] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const requestLanguageChange = useCallback(
        (nextLanguageCode: string) => {
            const normalized = nextLanguageCode?.trim();
            if (!normalized) return;
            if (normalized === currentLanguageCode) return;

            if (!isDirty) {
                applyLanguage(normalized);
                return;
            }

            setPendingLanguageCode(normalized);
            setIsOpen(true);
        },
        [applyLanguage, currentLanguageCode, isDirty]
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setPendingLanguageCode(null);
    }, []);

    const saveAndSwitch = useCallback(async () => {
        if (!pendingLanguageCode) return;

        setIsSaving(true);
        try {
            const result: SaveCurrentChangesResult = await saveCurrentChanges();
            if (!result.ok) {
                if (result.reason === "validation") {
                    // close the popup so that the user can see the form and errors.
                    close();
                }
                return;
            }

            const next = pendingLanguageCode;
            close();
            applyLanguage(next);
        } finally {
            setIsSaving(false);
        }
    }, [applyLanguage, close, pendingLanguageCode, saveCurrentChanges]);

    const dontSaveAndSwitch = useCallback(() => {
        if (!pendingLanguageCode) return;

        const next = pendingLanguageCode;
        close();
        applyLanguage(next);
    }, [applyLanguage, close, pendingLanguageCode]);

    return {
        requestLanguageChange,
        modalProps: {
            show: isOpen,
            isSaving,
            onClose: close,
            onSave: saveAndSwitch,
            onDontSave: dontSaveAndSwitch,
        },
    };
};
